import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Mail, Lock, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { formatApiError } from "@/lib/api";

function GoogleButton({ onClick, label }) {
  return (
    <button type="button" onClick={onClick} data-testid="google-register-btn"
      className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-700 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 transition-colors hover:bg-slate-100">
      <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
      {label}
    </button>
  );
}

export default function Register() {
  const { register, googleLogin } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register(email, password, name);
      navigate("/tarifs");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || t("auth.registerFail"));
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0A0C10] px-4">
      <div className="w-full max-w-sm">
        <div className="mb-4 flex justify-end"><LanguageSwitcher /></div>
        <div className="mb-8 flex flex-col items-center">
          <img src="/logo.png" alt="Golden Terminal" className="mb-3 h-14 w-14 object-contain" />
          <h1 className="font-heading text-2xl font-bold text-white">{t("auth.registerTitle")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("auth.registerSub")}</p>
        </div>
        <div className="rounded-2xl border border-slate-800 bg-[#0E1118] p-6 shadow-2xl">
          <GoogleButton onClick={googleLogin} label={t("auth.google")} />
          <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-wider text-slate-600">
            <div className="h-px flex-1 bg-slate-800" /> {t("auth.or")} <div className="h-px flex-1 bg-slate-800" />
          </div>
          <form onSubmit={submit} className="space-y-3">
            <div className="relative">
              <User className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-500" />
              <input data-testid="register-name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("auth.name")}
                className="w-full rounded-lg border border-slate-700 bg-[#11141A] py-2.5 pl-10 pr-3 text-sm text-white placeholder-slate-500 outline-none focus:border-blue-500" />
            </div>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-500" />
              <input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("common.email")}
                className="w-full rounded-lg border border-slate-700 bg-[#11141A] py-2.5 pl-10 pr-3 text-sm text-white placeholder-slate-500 outline-none focus:border-blue-500" />
            </div>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-500" />
              <input data-testid="register-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("auth.pwd6")}
                className="w-full rounded-lg border border-slate-700 bg-[#11141A] py-2.5 pl-10 pr-3 text-sm text-white placeholder-slate-500 outline-none focus:border-blue-500" />
            </div>
            {error && <p data-testid="register-error" className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</p>}
            <button data-testid="register-submit" type="submit" disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-60">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} {t("landing.createAccount")}
            </button>
          </form>
        </div>
        <p className="mt-5 text-center text-sm text-slate-500">
          {t("auth.haveAccount")} <Link to="/login" data-testid="go-login" className="font-semibold text-blue-400 hover:text-blue-300">{t("auth.signin")}</Link>
        </p>
      </div>
    </div>
  );
}
